import './clinical-results.js';
import './media.js';
import {
  getScrollContainer,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

// Product-page modules deliberately use native forms and shared media/Quick View.
const reducedMotion = () =>
  matchMedia("(prefers-reduced-motion: reduce)").matches;
const wrap = (value, count) => (count ? (value + count) % count : 0);

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"], [data-formula-panel], [data-step-panel]')];
    this.addEventListener(
      "shopify:block:select",
      (event) => {
        const panel = event.target.closest('[role="tabpanel"], [data-formula-panel], [data-step-panel]');
        const index = this.panels.indexOf(panel);
        if (index >= 0) this.select(index);
        const ingredient = event.target.closest("[data-ingredient]");
        if (ingredient) ingredient.open = true;
      },
      { signal },
    );
    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.select(index), { signal });
      tab.addEventListener(
        "keydown",
        (event) => {
          let next;
          if (event.key === "ArrowRight")
            next = wrap(index + 1, this.tabs.length);
          if (event.key === "ArrowLeft")
            next = wrap(index - 1, this.tabs.length);
          if (event.key === "Home") next = 0;
          if (event.key === "End") next = this.tabs.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.select(next);
            this.tabs[next]?.focus({ preventScroll: this.localName === "product-gallery" });
          }
        },
        { signal },
      );
    });
    this.previous = this.querySelector("[data-previous]");
    this.next = this.querySelector("[data-next]");
    this.previous?.addEventListener(
      "click",
      () => this.select(this.index - 1),
      { signal },
    );
    this.next?.addEventListener(
      "click",
      () => this.select(this.index + 1),
      { signal },
    );
    this.addEventListener(
      "toggle",
      (event) => {
        const detail = event.target;
        if (
          !(detail instanceof HTMLDetailsElement) ||
          !detail.matches("[data-ingredient]") ||
          !detail.open
        )
          return;
        if (this.handleIngredientToggle?.(detail)) return;
        detail
          .closest('[role="tabpanel"], [data-formula-panel], [data-step-panel]')
          ?.querySelectorAll("[data-ingredient]")
          .forEach((item) => {
            if (item !== detail) item.open = false;
          });
      },
      { capture: true, signal },
    );
    this.select(
      Math.min(Number(this.dataset.default) || 0, this.tabs.length - 1),
    );
  }
  select(index) {
    this.index = Math.max(0, Math.min(index, this.panels.length - 1));
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.panels.length - 1;
    this.tabs.forEach((tab, i) => {
      tab.setAttribute("aria-selected", String(i === this.index));
      tab.tabIndex = i === this.index ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== this.index;
      if (panel.hidden)
        panel.querySelectorAll("video").forEach((video) => video.pause());
      else if (!reducedMotion() && this.localName !== "ingredient-explorer")
        panel.animate([{ opacity: 0.5 }, { opacity: 1 }], { duration: 250 });
    });
    const current = this.panels[this.index];
    const first = current?.querySelector("[data-ingredient]");
    if (first && !current.querySelector("[data-ingredient][open]"))
      first.open = true;
    current
      ?.querySelectorAll("responsive-section-media")
      .forEach((host) => host.sync?.());
  }
  disconnectedCallback() {
    this.abort?.abort();
  }
}

// Only the desktop Rinse source specifies animated ingredient selection.
// Formula keys belong to the reusable section interface, not native products.
class IngredientExplorer extends ProductTabs {
  connectedCallback() {
    this.ingredientAbort?.abort();
    this.finishIngredients();
    this.ingredientAnimations = new Map();
    super.connectedCallback();
    this.ingredientAbort = new AbortController();
    const signal = this.ingredientAbort.signal;
    this.ingredientDesktop = matchMedia("(min-width: 750px)");
    this.ingredientReducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
    const finish = () => this.finishIngredients();
    this.ingredientDesktop.addEventListener("change", finish, { signal });
    this.ingredientReducedMotion.addEventListener("change", finish, { signal });
    window.addEventListener("resize", finish, { signal });
    document.fonts?.addEventListener("loadingdone", finish, { signal });
    this.addEventListener("load", finish, { signal, capture: true });
    this.addEventListener("focusin", (event) => {
      for (const [details, state] of this.ingredientAnimations) {
        if (details.contains(event.target) && !state.summary.contains(event.target)) {
          if (!state.open) state.summary.focus({ preventScroll: true });
          this.finishIngredient(details, state);
        }
      }
    }, { signal });
    this.addEventListener("click", (event) => this.selectIngredient(event), { signal });
  }
  select(index) {
    this.finishIngredients();
    super.select(index);
    const tab = this.tabs[this.index];
    const tablist = tab?.closest('[role="tablist"]');
    if (!tablist || tablist.scrollWidth <= tablist.clientWidth) return;
    const bounds = tablist.getBoundingClientRect();
    const thumbnail = tab.getBoundingClientRect();
    const label = tab.lastElementChild?.getBoundingClientRect();
    const left = thumbnail.left - bounds.left - 6;
    const right = Math.max(thumbnail.right, label?.right || 0) - bounds.right + 6;
    if (left < 0 || right > 0) {
      tablist.scrollBy({ left: left < 0 ? left : right, behavior: 'instant' });
    }
  }
  handleIngredientToggle(details) {
    if (this.ingredientAnimations?.has(details)) return true;
    // An external authoring toggle should retain the native exclusive behavior.
    if (this.ingredientAnimations?.size) this.finishIngredients();
    return false;
  }
  selectIngredient(event) {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest("summary");
    const details = summary?.parentElement;
    if (!details?.matches("[data-ingredient]") || !this.contains(details)) return;
    const panel = details.closest("[data-formula-panel]");
    if (
      event.defaultPrevented || event.button > 0 || event.altKey ||
      event.ctrlKey || event.metaKey || event.shiftKey ||
      panel?.dataset.formulaKey !== "rinse" || panel.hidden ||
      !this.ingredientDesktop.matches || this.ingredientReducedMotion.matches ||
      typeof details.animate !== "function" || typeof summary.animate !== "function" ||
      event.target.closest("a, button, input, select, textarea, [contenteditable]")
    ) {
      // Native activation must win after a modified click or runtime fallback.
      this.finishIngredients();
      return;
    }
    event.preventDefault();
    const active = this.ingredientAnimations.get(details);
    const open = !(active ? active.open : details.open);
    // Capture all current heights/colors before releasing a reversed animation.
    const entries = [...panel.querySelectorAll("[data-ingredient]")]
      .filter((item) => item === details || item.open || this.ingredientAnimations.has(item))
      .map((item) => ({
        details: item,
        summary: item.querySelector("summary"),
        open: item === details && open,
        from: item.getBoundingClientRect().height,
        color: getComputedStyle(item.querySelector("summary")).color,
      }));
    for (const entry of entries) {
      const previous = this.ingredientAnimations.get(entry.details);
      if (previous) this.finishIngredient(entry.details, previous, false);
    }
    for (const entry of entries) this.animateIngredient(entry);
  }
  animateIngredient({ details, summary, open, from, color }) {
    const state = {
      summary, open, inert: [],
      overflow: details.style.getPropertyValue("overflow"),
      overflowPriority: details.style.getPropertyPriority("overflow"),
    };
    this.ingredientAnimations.set(details, state);
    // Read the native final geometry and CSS selection color. Do not hardcode
    // authored body height or copy, nor infer a body-opacity transition.
    details.open = open;
    const to = details.getBoundingClientRect().height;
    const targetColor = getComputedStyle(summary).color;
    details.open = true;
    if (!open) {
      if (details.contains(document.activeElement) && !summary.contains(document.activeElement))
        summary.focus({ preventScroll: true });
      for (const child of details.children) {
        if (child === summary) continue;
        state.inert.push([child, child.getAttribute("inert")]);
        child.setAttribute("inert", "");
      }
    }
    details.style.setProperty("overflow", "hidden");
    try {
      const options = { duration: 300, easing: "ease-out", fill: "both" };
      state.colorAnimation = summary.animate([{ color }, { color: targetColor }], options);
      state.animation = details.animate([{ height: `${from}px` }, { height: `${to}px` }], options);
      state.animation.onfinish = () => this.finishIngredient(details, state);
      state.animation.oncancel = () => this.finishIngredient(details, state);
    } catch {
      this.finishIngredient(details, state);
    }
  }
  finishIngredient(details, state, commit = true) {
    if (this.ingredientAnimations?.get(details) !== state) return;
    this.ingredientAnimations.delete(details);
    if (state.animation) {
      state.animation.onfinish = null;
      state.animation.oncancel = null;
      state.animation.cancel();
    }
    state.colorAnimation?.cancel();
    if (commit) details.open = state.open;
    if (state.overflow)
      details.style.setProperty("overflow", state.overflow, state.overflowPriority);
    else details.style.removeProperty("overflow");
    for (const [child, value] of state.inert) {
      if (value === null) child.removeAttribute("inert");
      else child.setAttribute("inert", value);
    }
  }
  finishIngredients() {
    for (const [details, state] of this.ingredientAnimations || [])
      this.finishIngredient(details, state);
  }
  disconnectedCallback() {
    this.ingredientAbort?.abort();
    this.finishIngredients();
    super.disconnectedCallback();
  }
}

class ProductResults extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    scrollContainerMediaQuery.addEventListener("change", () => {
      this.disconnectedCallback();
      this.connectedCallback();
    }, { signal });
    this.observe?.disconnect();
    this.layoutObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.classList.remove("product-results--scroll");
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener(
      "change",
      () => this.connectedCallback(),
      { signal },
    );
    this.cards = [...this.querySelectorAll("[data-result]")];
    this.dots = [...this.querySelectorAll("[data-result-select]")];
    this.index = 0;
    if (this.classList.contains("product-results--cards")) {
      if (this.hasAttribute("data-cards-scroll")) {
        const track = this.querySelector("[data-result-track]");
        const previous = this.querySelector("[data-previous]");
        const next = this.querySelector("[data-next]");
        const sync = () => {
          if (!track) return;
          const left = track.getBoundingClientRect().left;
          this.index = this.cards.reduce((best, card, index) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[best].getBoundingClientRect().left - left) ? index : best, 0);
          if (previous) previous.disabled = track.scrollLeft <= 1;
          if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        };
        const move = delta => {
          const card = this.cards[Math.max(0, Math.min(this.cards.length - 1, this.index + delta))];
          if (card && track) track.scrollBy({left: card.getBoundingClientRect().left - track.getBoundingClientRect().left, behavior: reducedMotion() ? "instant" : "smooth"});
        };
        previous?.addEventListener("click", () => move(-1), {signal});
        next?.addEventListener("click", () => move(1), {signal});
        track?.addEventListener("scroll", sync, {signal, passive:true});
        this.layoutObserver = new ResizeObserver(sync);
        if (track) this.layoutObserver.observe(track);
        sync();
      }
      return;
    }
    this.dots.forEach((dot) =>
      dot.addEventListener(
        "click",
        () => this.choose(Number(dot.dataset.resultSelect)),
        { signal },
      ),
    );
    this.querySelector("[data-next]")?.addEventListener(
      "click",
      () => this.choose(Math.min(this.index + 1, this.cards.length - 1)),
      { signal },
    );
    this.querySelector("[data-previous]")?.addEventListener(
      "click",
      () => this.choose(Math.max(this.index - 1, 0)),
      { signal },
    );
    this.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target.closest("input,button,a")) return;
        this.pointerStart = event.clientX;
      },
      { signal },
    );
    this.addEventListener(
      "pointerup",
      (event) => {
        if (this.pointerStart === undefined) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = undefined;
        if (Math.abs(distance) > 50)
          this.choose(
            Math.max(0, Math.min(this.index + (distance < 0 ? 1 : -1), this.cards.length - 1)),
          );
      },
      { signal },
    );
    this.addEventListener("pointercancel", () => {
      this.pointerStart = undefined;
    }, { signal });
    if (
      this.hasAttribute("data-scroll-states") &&
      this.cards.length > 1 &&
      !reducedMotion()
    ) {
      // During a dialog, restore only a previously measured layout. New
      // instances stay in natural flow until their first fit check can run.
      if (this.pinnedLayout) this.classList.add("product-results--scroll");
      this.style.setProperty("--result-count", this.cards.length);
      this.layoutObserver = new ResizeObserver(() => this.syncPinnedLayout());
      for (const element of [
        this.querySelector(".product-results__intro"),
        this.querySelector(".product-results__media"),
        document.querySelector("product-page-sticky"),
      ]) {
        if (element) this.layoutObserver.observe(element);
      }
      window.addEventListener("resize", () => this.syncPinnedLayout(), { signal });
      // Reconcile viewport changes deferred while a drawer was open, even if
      // the sticky action stays hidden and has no size change on close.
      document.addEventListener("close", () => this.syncPinnedLayout(), { capture: true, signal });
      this.syncPinnedLayout();
      this.observe = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.fromScroll();
      }, { root: getIntersectionRoot() });
      this.observe.observe(this);
      getScrollEventTarget().addEventListener(
        "scroll",
        () => {
          if (this.inView && !this.frame)
            this.frame = requestAnimationFrame(() => {
              this.frame = null;
              this.fromScroll();
            });
        },
        { passive: true, signal },
      );
    }
    this.select(0);
  }
  syncPinnedLayout() {
    // Drawers temporarily hide sticky ATC. Its zero height must not change
    // whether this preceding module pins and shift the page behind the drawer.
    if (document.querySelector("dialog[open]")) return;
    const intro = this.querySelector(".product-results__intro");
    const media = this.querySelector(".product-results__media");
    if (!intro || !media) return;
    this.classList.add("product-results--scroll");
    const bottom = Math.max(
      (parseFloat(getComputedStyle(intro).top) || 0) + intro.offsetHeight,
      (parseFloat(getComputedStyle(media).top) || 0) + media.offsetHeight,
    );
    const stickyHeight = document.querySelector("product-page-sticky")?.getBoundingClientRect().height || 0;
    if (bottom > innerHeight - stickyHeight) {
      this.classList.remove("product-results--scroll");
    }
    this.pinnedLayout = this.classList.contains("product-results--scroll");
  }
  fromScroll() {
    if (!this.classList.contains("product-results--scroll")) return;
    const bounds = this.getBoundingClientRect();
    const distance = Math.max(1, bounds.height - innerHeight);
    const progress = Math.max(0, Math.min(0.999, -bounds.top / distance));
    this.select(Math.floor(progress * this.cards.length));
  }
  choose(index) {
    this.select(index);
    if (!this.classList.contains("product-results--scroll")) return;
    const bounds = this.getBoundingClientRect();
    const distance = Math.max(1, bounds.height - innerHeight);
    getScrollContainer().scrollBy({
      top: bounds.top + ((index + 0.25) / this.cards.length) * distance,
      behavior: reducedMotion() ? "auto" : "smooth",
    });
  }
  select(index) {
    this.index = index;
    const previous = this.querySelector("[data-previous]");
    const next = this.querySelector("[data-next]");
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === this.cards.length - 1;
    this.cards.forEach((card, i) => {
      card.hidden = i !== index;
    });
    this.dots.forEach((dot) =>
      dot.setAttribute(
        "aria-pressed",
        String(Number(dot.dataset.resultSelect) === index),
      ),
    );
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observe?.disconnect();
    this.layoutObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

class ProductComparison extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.labelObserver?.disconnect();
    this.abort = new AbortController();
    this.range = this.querySelector("[data-compare]");
    if (!this.range) return;
    this.labels = [...this.querySelectorAll(".product-comparison__labels span")];
    const sync = () => {
      const value = this.range.value;
      this.style.setProperty("--comparison", `${value}%`);
      const valueText = this.dataset.valueText;
      if (valueText) {
        this.range.setAttribute(
          "aria-valuetext",
          valueText
            .replaceAll("{before}", value)
            .replaceAll("{after}", String(100 - Number(value))),
        );
      }
      this.updateLabels();
    };
    this.range.addEventListener("input", sync, { signal: this.abort.signal });
    this.labelObserver = new ResizeObserver(() => this.updateLabels());
    [this, ...this.labels].forEach((element) => this.labelObserver.observe(element));
    sync();
  }
  updateLabels() {
    if (!this.labels.length) return;
    const bounds = this.getBoundingClientRect();
    if (!bounds.width) return;
    const divider = bounds.left + bounds.width * Number(this.range.value) / 100;
    const [before, after] = this.labels;
    // Measure in viewport coordinates so nested gallery masks share the same boundary.
    before?.classList.toggle("is-hidden", divider < before.getBoundingClientRect().right);
    after?.classList.toggle("is-hidden", divider > after.getBoundingClientRect().left);
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.labelObserver?.disconnect();
  }
}

class ProductPageSticky extends HTMLElement {
  connectedCallback() {
    this.sizeObserver?.disconnect();
    this.sizeObserver = new ResizeObserver(() => {
      const height = this.getBoundingClientRect().height;
      if (height > 0) document.body.style.setProperty('--product-page-sticky-height', `${height}px`);
    });
    this.sizeObserver.observe(this);
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    scrollContainerMediaQuery.addEventListener("change", () => {
      this.disconnectedCallback();
      this.connectedCallback();
    }, { signal });
    this.section = document.getElementById(this.dataset.section);
    if (!this.section) return;
    this.button = this.querySelector("[data-sticky-submit]");
    const cartDialog = document.querySelector("#cart-drawer dialog");
    const purchaseDialog = document.querySelector('#quick-add-dialog dialog');
    this.restorePurchaseFocus = false;
    purchaseDialog?.addEventListener('close', () => {
      if (!this.restorePurchaseFocus) return;
      this.restorePurchaseFocus = false;
      this.visibility();
      if (!this.hidden) this.button?.focus({ preventScroll: true });
    }, { signal });
    this.restoreCartFocus = false;
    this.button?.addEventListener(
      "blur",
      () => { this.restoreCartFocus = Boolean(cartDialog?.open); },
      { signal },
    );
    cartDialog?.addEventListener(
      "close",
      () => {
        // The shared drawer restores focus before the visibility observer runs.
        // Reveal the sticky trigger first when focus moved from it into the cart.
        this.visibility();
        if (this.restoreCartFocus && !this.hidden)
          this.button?.focus({ preventScroll: true });
        this.restoreCartFocus = false;
      },
      { signal },
    );
    this.button?.addEventListener(
      "click",
      (event) => {
        this.restorePurchaseFocus = true;
        this.querySelector('quick-add-component')?.handleClick(event);
      },
      { signal },
    );
    // A direct jump can move the form from below to above the viewport without
    // an intersection change. Check its current position on scroll as well.
    getScrollEventTarget().addEventListener("scroll", () => this.visibility(), {
      passive: true,
      signal,
    });
    this.section.addEventListener(
      "change",
      () => queueMicrotask(() => this.sync()),
      { signal },
    );
    this.section.addEventListener(
      "product:variant-change",
      () => this.bindForm(),
      { signal },
    );
    this.mutation = new MutationObserver(() => this.bindForm());
    this.mutation.observe(
      this.section.querySelector("[data-product-page]") || this.section,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["disabled"],
      },
    );
    this.dialogMutation = new MutationObserver(() => this.visibility());
    this.dialogMutation.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["open"],
    });
    const footer = document.querySelector(
      "footer, .shopify-section-group-footer-group",
    );
    if (footer) {
      this.footerObserve = new IntersectionObserver(([entry]) => {
        this.atFooter = entry.isIntersecting;
        this.visibility();
      }, { root: getIntersectionRoot() });
      this.footerObserve.observe(footer);
    }
    this.bindForm();
  }
  bindForm() {
    const form = this.section.querySelector("product-form-component form");
    if (this.form !== form) {
      this.form = form;
      this.observe?.disconnect();
      if (form) {
        this.observe = new IntersectionObserver(([entry]) => {
          this.pastForm =
            !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.visibility();
        }, { root: getIntersectionRoot() });
        this.observe.observe(form);
      }
    }
    this.sync();
  }
  sync() {
    const options = this.section.querySelector("theme-purchase-options");
    const subscription = options?.querySelector("[data-subscription]:checked");
    const allocation = options?.querySelector("[data-delivery] option:checked");
    const price =
      subscription && allocation
        ? allocation.dataset.price
        : options?.querySelector(
            ".theme-purchase-options__one-time > span:last-child",
          )?.textContent;
    const stickyPrice = this.querySelector("[data-sticky-price]");
    if (price && stickyPrice && stickyPrice.textContent !== price)
      stickyPrice.textContent = price;
    const submit = this.form?.querySelector('button[type="submit"]');
    const compare = this.querySelector("[data-sticky-compare]");
    const oneTimePrice = options?.querySelector(
      ".theme-purchase-options__one-time > span:last-child",
    )?.textContent;
    if (compare) {
      compare.hidden =
        !subscription ||
        !allocation ||
        Number(allocation.dataset.saving || 0) <= 0;
      if (oneTimePrice && compare.textContent !== oneTimePrice)
        compare.textContent = oneTimePrice;
    }
    const label = subscription
      ? this.button.dataset.subscriptionLabel
      : submit?.textContent?.trim();
    const buttonLabel =
      this.button.querySelector("[data-sticky-submit-label]") || this.button;
    if (label && buttonLabel.textContent !== label)
      buttonLabel.textContent = label;
    const disabled = !submit || submit.disabled;
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    this.visibility();
  }
  visibility() {
    if (this.form?.getBoundingClientRect) {
      this.pastForm = this.form.getBoundingClientRect().bottom < 0;
    }
    this.hidden =
      !this.pastForm ||
      this.atFooter ||
      Boolean(document.querySelector("dialog[open]"));
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observe?.disconnect();
    this.form = null;
    this.mutation?.disconnect();
    this.dialogMutation?.disconnect();
    this.footerObserve?.disconnect();
    this.sizeObserver?.disconnect();
    document.body.style.removeProperty('--product-page-sticky-height');
  }
}

for (const [name, constructor] of [
  ["ingredient-explorer", IngredientExplorer],
  ["product-steps", class extends ProductTabs {}],
  ["product-results", ProductResults],
  ["product-comparison", ProductComparison],
  ["product-page-sticky", ProductPageSticky],
]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
class ProductTimeline extends HTMLElement {
  async connectedCallback() {
    this.disconnectedCallback();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    await customElements.whenDefined("results-timeline");
    if (signal.aborted) return;
    const timeline = this.querySelector("results-timeline");
    this.cards = [...this.querySelectorAll("[data-results-timeline-card]")];
    this.index = this.cards.findIndex((card) => card.getAttribute("aria-pressed") === "true");
    timeline?.addEventListener("click", (event) => {
      const card = event.target.closest("[data-results-timeline-card]");
      const index = this.cards.indexOf(card);
      if (index < 0 || index === this.index) return;
      this.cards.forEach((item) => {
        item.toggleAttribute("data-timeline-detail-reveal", item === card);
      });
      this.index = index;
    }, { signal });
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.cards?.forEach((card) => card.removeAttribute("data-timeline-detail-reveal"));
  }
}

class ProductMediaSequence extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.videos = [...this.querySelectorAll("video")];
    this.videos.forEach((video) => {
      video.loop = this.dataset.playback === "loop";
      if (video.loop) return;
      video.addEventListener(
        "ended",
        () => {
          if (reducedMotion()) return;
          const candidates = this.hasAttribute("data-cycle")
            ? this.videos.filter((item) => item.getClientRects().length && !item.closest('[hidden], [aria-hidden="true"]'))
            : this.videos;
          const nextIndex = candidates.indexOf(video) + 1;
          const next = candidates[this.hasAttribute("data-cycle") ? nextIndex % candidates.length : nextIndex];
          if (next && this.hasAttribute("data-cycle")) {
            next.currentTime = 0;
            next.muted = true;
            this.sequenceVideo = next;
            this.sequencePending = true;
          }
          const track = this.querySelector("[data-theme-scroll]");
          if (next && track)
            track.scrollBy({
              left:
                next.getBoundingClientRect().left -
                track.getBoundingClientRect().left,
              behavior: "smooth",
            });
          if (this.hasAttribute("data-cycle")) this.sync();
        },
        { signal },
      );
    });
    this.addEventListener("theme:media-change", () => this.sync(), { signal });
    if (this.hasAttribute("data-cycle")) {
      this.sequenceVideo = null;
      this.sequencePending = false;
      const track = this.querySelector("[data-theme-scroll]");
      const cancelPendingSequence = (event) => {
        if (!event.isTrusted || !this.sequencePending) return;
        this.sequencePending = false;
        this.sequenceVideo = null;
        this.sync();
      };
      // A vertical page scroll or a tap must not take over a horizontal handoff.
      track?.addEventListener("wheel", (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY !== 0)) {
          cancelPendingSequence(event);
        }
      }, { signal, passive: true, capture: true });
      let pointerStart;
      track?.addEventListener("pointerdown", (event) => {
        if (!event.isTrusted || event.isPrimary === false || event.button !== 0) return;
        const mouse = event.pointerType === "mouse";
        if (mouse && (track.scrollWidth <= track.clientWidth || event.target.closest("input, select, textarea") ||
          (event.target.closest("button") && !track.hasAttribute("data-theme-drag-buttons")))) return;
        pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY, mouse, axis: null };
      }, { signal, passive: true, capture: true });
      track?.addEventListener("pointermove", (event) => {
        if (!event.isTrusted || !pointerStart || event.pointerId !== pointerStart.id) return;
        const dx = Math.abs(event.clientX - pointerStart.x);
        const dy = Math.abs(event.clientY - pointerStart.y);
        // ThemeScroller keeps mouse drags active regardless of their vertical path.
        if (pointerStart.mouse) {
          if (dx >= 5) cancelPendingSequence(event);
          return;
        }
        if (!pointerStart.axis && Math.max(dx, dy) >= 5) pointerStart.axis = dx > dy ? "x" : "y";
        if (pointerStart.axis === "x") cancelPendingSequence(event);
      }, { signal, passive: true, capture: true });
      const clearPointer = (event) => {
        if (!event || event.pointerId === pointerStart?.id) pointerStart = null;
      };
      window.addEventListener("pointerup", clearPointer, { signal });
      window.addEventListener("pointercancel", clearPointer, { signal });
      track?.addEventListener("lostpointercapture", clearPointer, { signal });
      // Native touch panning can cancel pointer events before a swipe is known.
      let touchStart;
      track?.addEventListener("touchstart", (event) => {
        const touch = event.touches[0];
        touchStart = event.isTrusted && event.touches.length === 1
          ? { id: touch.identifier, x: touch.clientX, y: touch.clientY, axis: null }
          : null;
      }, { signal, passive: true, capture: true });
      track?.addEventListener("touchmove", (event) => {
        if (!event.isTrusted || !touchStart) return;
        const touch = event.touches.length === 1 && event.touches[0];
        if (!touch || touch.identifier !== touchStart.id) { touchStart = null; return; }
        const dx = Math.abs(touch.clientX - touchStart.x);
        const dy = Math.abs(touch.clientY - touchStart.y);
        if (!touchStart.axis && Math.max(dx, dy) >= 5) touchStart.axis = dx > dy ? "x" : "y";
        if (touchStart.axis === "x") cancelPendingSequence(event);
      }, { signal, passive: true, capture: true });
      const clearTouch = () => { touchStart = null; };
      track?.addEventListener("touchend", clearTouch, { signal });
      track?.addEventListener("touchcancel", clearTouch, { signal });
      window.addEventListener("blur", () => { clearPointer(); clearTouch(); }, { signal });
      track?.addEventListener("keydown", (event) => {
        if (event.target === track && !event.altKey && !event.ctrlKey && !event.metaKey && ["ArrowLeft", "ArrowRight"].includes(event.key)) {
          cancelPendingSequence(event);
        }
      }, { signal });
      this.addEventListener("click", (event) => {
        const control = event.target.closest("[data-theme-video-playback]");
        if (control) {
          this.sequenceVideo = control.closest("responsive-section-media")?.activeVideo?.();
          this.sequencePending = false;
        }
      }, {signal, capture: true});
      this.videos.forEach((video) => video.addEventListener("play", () => this.sync(), { signal }));
      document.addEventListener("visibilitychange", () => this.sync(), { signal });
      window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", () => this.sync(), { signal });
    }
    this.querySelector("[data-theme-scroll]")?.addEventListener(
      "scroll",
      () => this.sync(),
      { signal, passive: true },
    );
  }
  sync() {
    if (this.syncing) return;
    this.syncing = true;
    const visible = this.videos.filter((video) => {
      const rect = video.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < innerHeight &&
        rect.right > 0 &&
        rect.left < innerWidth
      );
    });
    const nearest = visible.reduce((chosen, video) => {
      const rect = video.getBoundingClientRect();
      const center = (rect.left + rect.right) / 2;
      return !chosen ||
        Math.abs(center - innerWidth / 2) <
          Math.abs(chosen.center - innerWidth / 2)
        ? { video, center }
        : chosen;
    }, null)?.video;
    if (this.sequenceVideo && visible.includes(this.sequenceVideo)) this.sequencePending = false;
    if (this.sequenceVideo && !visible.includes(this.sequenceVideo) && !this.sequencePending) this.sequenceVideo = null;
    const active = this.hasAttribute("data-cycle") && this.sequenceVideo
      ? (visible.includes(this.sequenceVideo) ? this.sequenceVideo : null)
      : nearest;
    this.videos.forEach((video) => {
      if (video !== active || reducedMotion() || document.hidden) {
        video.pause();
        video.muted = true;
      }
    });
    if (this.hasAttribute("data-cycle") && active?.ended && !reducedMotion() && !document.hidden) active.currentTime = 0;
    if (active && !active.ended && !reducedMotion() && !document.hidden)
      active.closest("responsive-section-media")?.sync?.();
    this.syncing = false;
  }
  disconnectedCallback() {
    this.abort?.abort();
  }
}
if (!customElements.get("product-timeline"))
  customElements.define("product-timeline", ProductTimeline);
if (!customElements.get("product-media-sequence"))
  customElements.define("product-media-sequence", ProductMediaSequence);
class ProductGallery extends ProductTabs {
  connectedCallback() {
    const thumbnails = this.querySelector(".product-gallery__thumbnails");
    this.querySelectorAll('.product-gallery__stage [role="tab"]').forEach(
      (tab) => thumbnails?.append(tab),
    );
    const stage = this.querySelector(".product-gallery__stage");
    this.track = stage?.querySelector(".product-gallery__track");
    if (stage && !this.track) {
      this.track = document.createElement("div");
      this.track.className = "product-gallery__track";
      const items = [...stage.children].filter((child) =>
        child.matches(".product-gallery__item") || child.querySelector(".product-gallery__item"),
      );
      this.track.append(...items);
      stage.prepend(this.track);
    }
    super.connectedCallback();
    // Keep the authored pill with the first tile, including during gallery scroll.
    const badge = this.closest('[data-product-page]')?.querySelector(':scope > .product-page-section__badge');
    if (badge && this.panels[0]) this.panels[0].append(badge);
    const signal = this.abort.signal;
    this.bindThumbnailDrag(thumbnails, signal);
    this.bindMediaDrag(signal);
    let scrollTimer;
    this.track?.addEventListener("scroll", () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => this.syncScrollSelection(), 120);
    }, { signal, passive: true });
    this.track?.addEventListener("scrollend", () => {
      clearTimeout(scrollTimer);
      this.syncScrollSelection();
    }, { signal });
    signal.addEventListener("abort", () => clearTimeout(scrollTimer), { once: true });
    this.galleryResize?.disconnect();
    this.galleryResize = new ResizeObserver(() => this.scrollToSelected(false));
    if (this.track) this.galleryResize.observe(this.track);
  }
  scrollToSelected(animate = true) {
    const panel = this.panels?.[this.index];
    if (!this.track || !panel) return;
    const distance = panel.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollTo({
      left: this.track.scrollLeft + distance,
      behavior: animate && !reducedMotion() ? "smooth" : "instant",
    });
  }
  syncScrollSelection() {
    if (!this.track || this.draggingMedia) return;
    const left = this.track.getBoundingClientRect().left;
    const nearest = this.panels.reduce((best, panel, index) =>
      Math.abs(panel.getBoundingClientRect().left - left) <
      Math.abs(this.panels[best].getBoundingClientRect().left - left) ? index : best, 0);
    if (nearest !== this.index) this.select(nearest, false);
  }
  bindMediaDrag(signal) {
    const track = this.track;
    if (!track) return;
    let drag;
    track.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0 ||
          event.target.closest("button,input,a,video,iframe,model-viewer")) return;
      drag = { id: event.pointerId, start: event.clientX, scroll: track.scrollLeft };
    }, { signal });
    track.addEventListener("pointermove", (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = event.clientX - drag.start;
      if (!this.draggingMedia && Math.abs(distance) < 5) return;
      this.draggingMedia = true;
      track.setPointerCapture(event.pointerId);
      track.style.scrollSnapType = "none";
      track.scrollLeft = drag.scroll - distance;
      event.preventDefault();
    }, { signal });
    const endDrag = (event) => {
      if (!drag || (event.type !== "blur" && event.pointerId !== drag.id)) return;
      const id = drag.id;
      drag = undefined;
      const dragged = this.draggingMedia;
      this.draggingMedia = false;
      track.style.removeProperty("scroll-snap-type");
      if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
      if (dragged) {
        this.syncScrollSelection();
        this.scrollToSelected();
      }
    };
    window.addEventListener("pointerup", endDrag, { signal });
    window.addEventListener("pointercancel", endDrag, { signal });
    window.addEventListener("blur", endDrag, { signal });
    track.addEventListener("lostpointercapture", endDrag, { signal });
  }
  bindThumbnailDrag(track, signal) {
    if (!track) return;
    let drag;
    let dragged = false;
    // The shared theme scroller is horizontal; this rail is vertical on desktop.
    // Keep touch scrolling native and use the same mouse/click boundary here.
    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      const vertical = getComputedStyle(track).flexDirection === "column";
      const overflow = vertical
        ? track.scrollHeight - track.clientHeight
        : track.scrollWidth - track.clientWidth;
      if (overflow <= 1) return;
      drag = {
        id: event.pointerId,
        vertical,
        start: vertical ? event.clientY : event.clientX,
        scroll: vertical ? track.scrollTop : track.scrollLeft,
      };
      dragged = false;
    }, { signal });
    track.addEventListener("pointermove", (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = (drag.vertical ? event.clientY : event.clientX) - drag.start;
      if (!dragged && Math.abs(distance) < 5) return;
      dragged = true;
      track.setPointerCapture(event.pointerId);
      if (drag.vertical) track.scrollTop = drag.scroll - distance;
      else track.scrollLeft = drag.scroll - distance;
      event.preventDefault();
    }, { signal });
    const endDrag = (event) => {
      if (!drag || (event.type !== "blur" && event.pointerId !== drag.id)) return;
      const id = drag.id;
      drag = undefined;
      if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
    };
    window.addEventListener("pointerup", endDrag, { signal });
    window.addEventListener("pointercancel", endDrag, { signal });
    window.addEventListener("blur", endDrag, { signal });
    track.addEventListener("lostpointercapture", endDrag, { signal });
    track.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
    track.addEventListener("click", (event) => {
      if (!dragged || event.detail === 0) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, { signal, capture: true });
  }
  select(index, scroll = true) {
    this.index = Math.max(0, Math.min(index, this.panels.length - 1));
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.panels.length - 1;
    this.tabs.forEach((tab, i) => {
      tab.setAttribute("aria-selected", String(i === this.index));
      tab.tabIndex = i === this.index ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = false;
      panel.inert = i !== this.index;
      panel.setAttribute("aria-hidden", String(i !== this.index));
      if (i !== this.index) {
        panel.querySelectorAll("deferred-media").forEach((media) => {
          media.pauseMedia?.();
          const iframe = media.querySelector("iframe");
          if (!iframe) return;
          // A player still loading can miss pause messages and autoplay later.
          // Unload it now; revisiting the tile loads a fresh player.
          iframe.remove();
          media.removeAttribute("data-media-loaded");
          media.querySelector('[ref="deferredMediaPlayButton"]')?.classList.remove("deferred-media__playing");
          media.querySelector('[ref="toggleMediaButton"]')?.classList.add("hidden");
        });
        panel.querySelectorAll("video").forEach((video) => video.pause());
      }
    });
    if (!reducedMotion() && !document.hidden) {
      this.panels[this.index]?.querySelectorAll("deferred-media").forEach((media) => {
        // Reveal without focusing the player, which would move the gallery track.
        media.loadContent?.(false);
        media.playMedia?.();
      });
    }
    this.panels[this.index]?.querySelectorAll("responsive-section-media")
      .forEach((host) => host.sync?.());
    if (scroll) this.scrollToSelected(this.initializedGallery === true);
    this.initializedGallery = true;
    const thumbnail = this.tabs[this.index];
    const rail = this.querySelector(".product-gallery__thumbnails");
    if (!thumbnail || !rail) return;
    const vertical = getComputedStyle(rail).flexDirection === "column";
    const start = vertical ? "top" : "left";
    const end = vertical ? "bottom" : "right";
    const bounds = thumbnail.getBoundingClientRect();
    const viewport = rail.getBoundingClientRect();
    const distance = bounds[start] < viewport[start]
      ? bounds[start] - viewport[start]
      : Math.max(0, bounds[end] - viewport[end]);
    if (distance) rail.scrollBy({
      [start]: distance,
      behavior: reducedMotion() ? "auto" : "smooth",
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.galleryResize?.disconnect();
    this.track?.style.removeProperty("scroll-snap-type");
    this.draggingMedia = false;
    this.initializedGallery = false;
  }
}
if (!customElements.get("product-gallery"))
  customElements.define("product-gallery", ProductGallery);

// The PDP presents the authored subscription benefits as an expanded list. The
// native purchase component continues to own plan selection and form payloads.
class ProductPageComposition extends HTMLElement {
  connectedCallback() {
    this.disclosureAbort?.abort();
    this.finishDisclosures();
    this.disclosures = new Map();
    this.disclosureAbort = new AbortController();
    const signal = this.disclosureAbort.signal;
    this.querySelectorAll(".theme-purchase-options__benefits").forEach(
      (details) => { details.open = true; },
    );
    this.disclosureMotion = matchMedia("(prefers-reduced-motion: reduce)");
    const finish = () => this.finishDisclosures();
    this.disclosureMotion.addEventListener("change", finish, { signal });
    window.addEventListener("resize", finish, { signal });
    // A newly loaded image or font must not leave an outdated clipping height.
    this.addEventListener("load", (event) => {
      for (const [details, state] of this.disclosures) {
        if (details.contains(event.target)) this.finishDisclosure(details, state);
      }
    }, { signal, capture: true });
    document.fonts?.addEventListener("loadingdone", finish, { signal });
    this.addEventListener("focusin", (event) => {
      for (const [details, state] of this.disclosures) {
        if (details.contains(event.target) && !state.summary.contains(event.target)) {
          if (!state.open) state.summary.focus({ preventScroll: true });
          this.finishDisclosure(details, state);
        }
      }
    }, { signal });
    this.addEventListener("click", (event) => this.toggleDisclosure(event), { signal });
  }
  toggleDisclosure(event) {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest("summary");
    const details = summary?.parentElement;
    if (
      !details?.matches(".product-info-entry, .product-info-item details") ||
      !this.contains(details)
    ) return;
    if (
      event.defaultPrevented || event.button > 0 || event.altKey ||
      event.ctrlKey || event.metaKey || event.shiftKey ||
      event.target.closest("a, button, input, select, textarea, [contenteditable]")
    ) {
      // Release our effect before handing activation back to native details.
      this.finishDisclosures();
      return;
    }
    const active = this.disclosures.get(details);
    const open = !(active ? active.open : details.open);
    const group = details.closest('.product-info-accordion');
    if (open && group) {
      const anchorTop = summary.getBoundingClientRect().top;
      group.querySelectorAll('.product-info-entry[open]').forEach((other) => {
        if (other === details || other.contains(details)) return;
        const state = this.disclosures.get(other);
        if (state) this.finishDisclosure(other, state);
        other.open = false;
      });
      const shift = summary.getBoundingClientRect().top - anchorTop;
      if (shift) getScrollContainer().scrollBy({ top: shift, behavior: 'instant' });
    }
    if (this.disclosureMotion.matches || typeof details.animate !== "function") {
      this.finishDisclosures();
      return;
    }
    event.preventDefault();
    // Settle related motions before measuring; nested lists remain independent.
    for (const [other, state] of this.disclosures) {
      if (other !== details && (other.contains(details) || details.contains(other)))
        this.finishDisclosure(other, state);
    }
    const from = details.getBoundingClientRect().height;
    if (active) this.finishDisclosure(details, active, false);
    details.open = true;
    const styles = getComputedStyle(details);
    const number = (value) => parseFloat(value) || 0;
    const to = open ? details.getBoundingClientRect().height :
      summary.getBoundingClientRect().height + number(styles.paddingTop) +
      number(styles.paddingBottom) + number(styles.borderTopWidth) +
      number(styles.borderBottomWidth);
    const state = {
      open,
      summary,
      overflow: details.style.getPropertyValue("overflow"),
      overflowPriority: details.style.getPropertyPriority("overflow"),
      inert: [],
    };
    if (!open) {
      if (details.contains(document.activeElement) && !summary.contains(document.activeElement))
        summary.focus({ preventScroll: true });
      for (const child of details.children) {
        if (child === summary) continue;
        state.inert.push([child, child.getAttribute("inert")]);
        child.setAttribute("inert", "");
      }
    }
    this.disclosures.set(details, state);
    details.style.setProperty("overflow", "hidden");
    try {
      state.animation = details.animate(
        [{ height: `${from}px` }, { height: `${to}px` }],
        { duration: 300, easing: "ease-out", fill: "both" },
      );
      state.animation.onfinish = () => this.finishDisclosure(details, state);
      state.animation.oncancel = () => this.finishDisclosure(details, state);
    } catch {
      // Native open state remains usable when the browser cannot animate it.
      this.finishDisclosure(details, state);
    }
  }
  finishDisclosure(details, state, commit = true) {
    if (this.disclosures?.get(details) !== state) return;
    this.disclosures.delete(details);
    if (state.animation) {
      state.animation.onfinish = null;
      state.animation.oncancel = null;
      state.animation.cancel();
    }
    if (commit) details.open = state.open;
    if (state.overflow)
      details.style.setProperty("overflow", state.overflow, state.overflowPriority);
    else details.style.removeProperty("overflow");
    for (const [child, value] of state.inert) {
      if (value === null) child.removeAttribute("inert");
      else child.setAttribute("inert", value);
    }
  }
  finishDisclosures() {
    for (const [details, state] of this.disclosures || [])
      this.finishDisclosure(details, state);
  }
  disconnectedCallback() {
    this.disclosureAbort?.abort();
    this.finishDisclosures();
  }
}
if (!customElements.get("product-page-composition"))
  customElements.define("product-page-composition", ProductPageComposition);
